import React, { useState, useEffect } from 'react';
import { reservationsApi } from '../../services/api';
import { useNotify } from '../../context/ConfirmContext';

export default function NightAuditPage({ hotel, onSuccess, onNavigate }) {
    const notify = useNotify();
    const [reservations, setReservations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadAudit = async () => {
        if (!hotel) return;
        setLoading(true);
        setError(null);
        try {
            const data = await reservationsApi.nightAuditToday(hotel.id);
            setReservations(data);
        } catch (err) {
            setError(err.message || 'Failed to load night audit list');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadAudit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hotel]);

    const handleAudit = async (id) => {
        try {
            await reservationsApi.markAudited(id);
            if (onSuccess) onSuccess('Reservation audited successfully!');
            // Update local state
            setReservations(prev => prev.map(r => 
                r.id === id ? { ...r, is_audited: 1, audited_at: new Date().toISOString() } : r
            ));
        } catch (err) {
            await notify(err.message || 'Failed to mark as audited');
        }
    };

    if (loading) return <div className="p-card" style={{ padding: 24, textAlign: 'center' }}>Loading Night Audit...</div>;
    if (error) return <div className="p-card" style={{ padding: 24, color: 'var(--danger)' }}>{error}</div>;

    const pending = reservations.filter(r => !r.is_audited);
    const completed = reservations.filter(r => r.is_audited);
    const stillStaying = reservations.filter(r => r.status === 'checked-in');
    const checkedOut = reservations.filter(r => r.status === 'checked-out');

    const isAfter11PM = new Date().getHours() >= 23;

    return (
        <div style={{ maxWidth: 1000, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 24 }}>
            <div className="p-card" style={{ padding: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
                    <div>
                        <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>🌙 Night Audit</h2>
                        <div style={{ color: 'var(--muted)', fontSize: 14 }}>
                            All guests currently staying or who stayed today. Verify rooms, rates, and stay details.
                        </div>
                    </div>
                    <button className="btn btn-outline" onClick={loadAudit}>↻ Refresh</button>
                </div>

                {reservations.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--muted)', border: '1px dashed rgba(255,255,255,0.1)', borderRadius: 12 }}>
                        🌙 No active or same-day reservations found for today.
                    </div>
                ) : (
                    <>
                        <div style={{ marginBottom: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12 }}>
                            {[
                              { label: 'Pending Audit', value: pending.length, color: 'var(--gold)' },
                              { label: 'Verified', value: completed.length, color: 'var(--success)' },
                              { label: 'Still In Hotel', value: stillStaying.length, color: 'var(--teal)' },
                              { label: 'Checked Out', value: checkedOut.length, color: 'var(--muted)' },
                            ].map(s => (
                              <div key={s.label} style={{ background: 'rgba(255,255,255,0.02)', padding: 14, borderRadius: 8, borderLeft: `4px solid ${s.color}` }}>
                                <div style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--muted)', fontWeight: 700, letterSpacing: 1 }}>{s.label}</div>
                                <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)', marginTop: 4 }}>{s.value}</div>
                              </div>
                            ))}
                        </div>

                        <div style={{ overflowX: 'auto' }}>
                            <table className="dt-table">
                                <thead>
                                    <tr>
                                        <th>Guest</th>
                                        <th>Room</th>
                                        <th>Stay Dates</th>
                                        <th>Rate</th>
                                        <th>Stay Status</th>
                                        <th>Audit</th>
                                        <th style={{ textAlign: 'right' }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reservations.map(r => (
                                        <tr key={r.id}>
                                            <td>
                                                <div style={{ fontWeight: 600 }}>{r.guest_name}</div>
                                                <div style={{ fontSize: 12, color: 'var(--muted)' }}>{r.guest_phone || '—'}</div>
                                            </td>
                                            <td>
                                                <span className="room-badge">{r.room || 'Unassigned'}</span>
                                                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>{r.room_type}</div>
                                            </td>
                                            <td>
                                                <div style={{ fontSize: 13 }}>{r.check_in}</div>
                                                <div style={{ fontSize: 12, color: 'var(--muted)' }}>→ {r.check_out}</div>
                                            </td>
                                            <td>₹{r.room_rate}</td>
                                            <td>
                                                <span style={{
                                                    display: 'inline-block', padding: '2px 10px', borderRadius: 12,
                                                    background: r.status === 'checked-in' ? 'rgba(20,184,166,0.1)' : 'rgba(100,100,100,0.15)',
                                                    color: r.status === 'checked-in' ? 'var(--teal)' : 'var(--muted)',
                                                    fontSize: 12, fontWeight: 600
                                                }}>
                                                    {r.status === 'checked-in' ? '🏨 Staying' : '✅ Checked Out'}
                                                </span>
                                            </td>
                                            <td>
                                                {r.is_audited ? (
                                                    <span style={{ 
                                                        display: 'inline-block', padding: '2px 8px', borderRadius: 12, 
                                                        background: 'rgba(46, 204, 113, 0.1)', color: 'var(--success)', 
                                                        fontSize: 12, fontWeight: 600 
                                                    }}>
                                                        ✓ Verified
                                                    </span>
                                                ) : (
                                                    <span style={{ 
                                                        display: 'inline-block', padding: '2px 8px', borderRadius: 12, 
                                                        background: 'rgba(241, 196, 15, 0.1)', color: 'var(--gold)', 
                                                        fontSize: 12, fontWeight: 600 
                                                    }}>
                                                        Pending
                                                    </span>
                                                )}
                                                {r.is_audited && r.audited_by && (
                                                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>by {r.audited_by}</div>
                                                )}
                                            </td>
                                            <td style={{ textAlign: 'right', verticalAlign: 'middle' }}>
                                                {!r.is_audited ? (
                                                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                                        <button 
                                                            className="btn btn-ghost" 
                                                            style={{ padding: '6px 12px', fontSize: 13, border: '1px solid var(--border)' }}
                                                            onClick={() => onNavigate && onNavigate('reservations')}
                                                        >
                                                            ✏️ Edit
                                                        </button>
                                                        <button 
                                                            className="btn btn-gold" 
                                                            style={{ padding: '6px 16px', fontSize: 13, opacity: isAfter11PM ? 1 : 0.5, cursor: isAfter11PM ? 'pointer' : 'not-allowed' }}
                                                            onClick={async () => isAfter11PM ? handleAudit(r.id) : await notify('Night Audit can only be performed after 11:00 PM.')}
                                                            title={isAfter11PM ? 'Approve' : 'Audit unlocks at 11:00 PM'}
                                                        >
                                                            Approve & Verify
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <span style={{ color: 'var(--success)', fontSize: 20 }}>✓</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
